import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, Observable } from 'rxjs';
import { View } from '../../../../shared/store/view/types';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SubHeaderService } from '../../../../shared/store/subheader/subheader.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ViewService } from '../../../../shared/store/view/views.service';
import { FormService } from '../../../../shared/store/form/form.service';
import { CampaignService } from '../../../../shared/store/campaign/campaign.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { formatDate } from '../../../../shared/helper/datehelper';
import { take } from 'rxjs/operators';
import { CampaignModel } from '../../../../shared/store/campaign/types';
import { MAX_DATE_MASK_SAFE_GUARD } from '../../../../shared/constants/date.constants';

@UntilDestroy()
@Component({
  selector: 'app-campaign-create',
  templateUrl: './campaign-create.component.html',
  styleUrls: ['./campaign-create.component.scss'],
  standalone: false,
})
export class CampaignCreateComponent implements OnInit, OnDestroy {
  view$: Observable<View>;
  campaignForm: FormGroup;
  createCampaignInstance$: Observable<CampaignModel>;

  maxDateMaskSafeGuard: string = MAX_DATE_MASK_SAFE_GUARD;

  constructor(
    private readonly campaignService: CampaignService,
    private readonly router: Router,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formService: FormService,
    private readonly formBuilder: FormBuilder,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.createCampaignInstance$ = this.campaignService.createInstance.value$;
  }

  ngOnInit(): void {
    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'campaignCreate') this.onSubmit();
        if (x == 'campaignList') {
          this.formService.clear();
          this.router.navigate(['Crm/Campaigns/']);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
        this.breadcrumbService.set('@campaignCreate', x.breadcrumbCreate);
      });

    this.campaignForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      startDate: [''],
      endDate: [''],
    });

    this.campaignService.createInstance.call();
    this.createCampaignInstance$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        this.campaignForm?.patchValue({
          name: x.name,
          startDate: formatDate(x.startDate, 'ToInverseSqlDate'),
          endDate: formatDate(x.endDate, 'ToInverseSqlDate'),
        });
      });
  }

  onSubmit() {
    if (this.campaignForm.invalid) {
      this.campaignForm.markAllAsTouched();
      this.formService.countErrors(this.campaignForm, true);
      return;
    }

    this.formService.clear();

    let result$ = this.campaignService.postCampaign.call({
      id: 0,
      name: this.campaignForm.value.name!,
      startDate:
        this.campaignForm.value.startDate === ''
          ? null
          : this.campaignForm.value.startDate!,
      endDate:
        this.campaignForm.value.endDate === ''
          ? null
          : this.campaignForm.value.endDate!,
    });

    result$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.router.navigate(['/Crm/Campaigns/Details', result.id]);
      });
  }

  ngOnDestroy(): void {
    this.campaignService.postCampaign.reset();
  }
}
